// Configuración de la rotación automática de un área (dentro del formulario del área).
// Los servidores del área se turnan solos en las reuniones elegidas: sirve quien lleva
// más tiempo sin hacerlo y, si hay puestos, cada persona va cambiando de puesto.

export const esAreaInfantil = (nombre) => /infantil|kids/i.test(nombre || "");
// En Supabase el área trae la marca es_infantil; sin ella (API anterior) se deduce del nombre.
const esDeKids = (area) => area?.es_infantil ?? esAreaInfantil(area?.nombre);

export const textoPuestos = (puestos) => (Array.isArray(puestos) ? puestos.join(", ") : puestos || "");

export const listaPuestos = (puestos) => textoPuestos(puestos).split(",").map((p) => p.trim()).filter(Boolean);

// Resumen para la lista de áreas: "Rota sola · Servicio Dominical, Reunión de Miércoles · 3 puestos".
export function resumenRotacion(area, tiposServicio) {
  if (esDeKids(area)) return "Rotación de Mi Casa Kids: un profesor y un asistente cada domingo";
  if (!area.rotacion_automatica) return null;
  const reuniones = tiposServicio
    .filter((t) => (area.tipos_servicio_rotacion || []).includes(t.id))
    .map((t) => t.nombre)
    .join(", ");
  const puestos = listaPuestos(area.puestos_rotacion);
  const cupos = puestos.length ? `${puestos.join(", ")}` : `${area.personas_por_reunion || 1} persona(s) por reunión`;
  return `Rota sola · ${reuniones || "sin reuniones elegidas"} · ${cupos}`;
}

function ConfigRotacionArea({ form, setForm, tiposServicio, input, label }) {
  if (esDeKids(form)) {
    return (
      <div className="rounded-xl border border-gray-700 bg-oscuro/50 p-4">
        <p className="text-white text-sm font-semibold">Rotación de Mi Casa Kids</p>
        <p className="text-gray-400 text-xs mt-1">
          Cada domingo se asigna un profesor y un asistente; rotan por separado. Se administra en Servidores → Profesores.
        </p>
      </div>
    );
  }

  const tipos = form.tipos_servicio_rotacion || [];
  const tienePuestos = listaPuestos(form.puestos_rotacion).length > 0;
  const alternarTipo = (id) =>
    setForm({ ...form, tipos_servicio_rotacion: tipos.includes(id) ? tipos.filter((t) => t !== id) : [...tipos, id] });

  return (
    <div className="rounded-xl border border-gray-700 bg-oscuro/50 p-4 space-y-4">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(form.rotacion_automatica)}
          onChange={(e) => setForm({ ...form, rotacion_automatica: e.target.checked })}
          className="w-4 h-4 mt-0.5 accent-yellow-500"
        />
        <span>
          <span className="text-white text-sm font-semibold block">Rotación automática</span>
          <span className="text-gray-400 text-xs">
            Los servidores del área se turnan solos: sirve quien lleva más tiempo sin hacerlo y los demás descansan.
          </span>
        </span>
      </label>

      {form.rotacion_automatica && (
        <>
          <fieldset>
            <legend className={label}>Se usa en estas reuniones *</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {tiposServicio.map((tipo) => (
                <label key={tipo.id} className="flex items-center gap-2 text-sm text-gray-300 py-1 cursor-pointer">
                  <input type="checkbox" checked={tipos.includes(tipo.id)} onChange={() => alternarTipo(tipo.id)} className="w-4 h-4 accent-yellow-500" />
                  {tipo.nombre}
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label className={label} htmlFor="puestos-rotacion">Puestos en cada reunión</label>
            <input
              id="puestos-rotacion"
              value={textoPuestos(form.puestos_rotacion)}
              onChange={(e) => setForm({ ...form, puestos_rotacion: e.target.value })}
              className={input}
              placeholder="Ej: Letras, Pantalla, Sonido"
            />
            <p className="text-gray-500 text-xs mt-1">
              Separados por coma. Se pone una persona en cada puesto y van cambiando de puesto. Déjalo vacío si todos hacen lo mismo.
            </p>
          </div>

          {!tienePuestos && (
            <div>
              <label className={label} htmlFor="personas-por-reunion">Personas por reunión</label>
              <input
                id="personas-por-reunion"
                type="number"
                min={1}
                max={20}
                value={form.personas_por_reunion ?? 1}
                onChange={(e) => setForm({ ...form, personas_por_reunion: e.target.value })}
                className={input}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ConfigRotacionArea;
